/**
 * MusicSongsPage - 音楽ライブラリの「曲」一覧（Apple Music 相当）
 *
 * - 全曲を1回で取得し、並び替え・頭文字インデックス・検索をクライアント側で行う
 * - 歌詞やアーティストなど全文/関連テーブルが必要な絞り込みはサーバー側で行う
 * - 読み仮名を持たないため、漢字・記号・数字で始まる曲名は '#' セクションにまとめる
 * - キューは localStorage に永続化されるため、一度に入れるのは MAX_QUEUE_LENGTH 曲まで
 */

import { CircleArrowDown, Music, Play, Shuffle, SlidersHorizontal } from 'lucide-react'
import { Fragment, useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'

import { getQueueTracks } from '@/api/api'
import type { GetQueueTracksParams, QueueTrackItem } from '@/api/type'
import { Header } from '@/components/shared/header'
import { AuthImage } from '@/components/ui/auth-image'
import { SearchBox } from '@/components/ui/search-box'
import { Spinner } from '@/components/ui/spinner'
import { PATHS } from '@/routes/paths'
import { useMusicStore } from '@/store/useMusicStore'

import { ACCENT_COLOR, DOWNLOADED_ONLY_PARAM, TRACK_FETCH_LIMIT } from './constants'
import { IndexedList } from './indexed-list'
import { useOfflineMusicTracks } from './offline-music'
import { pickRandomTracks, sliceQueueWindow, toMusicTrack } from './queue-track'
import {
  DEFAULT_SONGS_SORT,
  defaultDescending,
  EMPTY_SONGS_FILTER,
  isSongsFilterActive,
  type SongsFilter,
  type SongsSort,
  SORT_OPTIONS,
  type SortKey,
} from './songs-filter'
import { SongsFilterSheet } from './songs-filter-sheet'
import { MusicTrackMenu } from './track-menu'

const TRACK_ROW_HEIGHT = 76

/** フィルタ条件をトラック取得APIのクエリへ変換する */
const toQueryParams = (filter: SongsFilter): GetQueueTracksParams => {
  const title = filter.title.trim()
  const isLyricsExcluded = filter.lyricsPresence === 'without'
  const lyricsQuery = isLyricsExcluded ? '' : filter.lyricsQuery.trim()
  const yearMin = Number(filter.releaseYearMin.trim())
  const yearMax = Number(filter.releaseYearMax.trim())
  return {
    source: 'all',
    limit: TRACK_FETCH_LIMIT,
    artist_tag_ids: filter.artistTagIds.length > 0 ? filter.artistTagIds.join(',') : undefined,
    title: title || undefined,
    lyrics_query: lyricsQuery || undefined,
    has_lyrics: filter.lyricsPresence === 'all' ? undefined : filter.lyricsPresence === 'with',
    lyrics_timings: !isLyricsExcluded && filter.lyricsTimings.length > 0 ? filter.lyricsTimings.join(',') : undefined,
    release_year_min: yearMin || undefined,
    release_year_max: yearMax || undefined,
  }
}

const getTrackKey = (track: QueueTrackItem) => `track-${track.episode_id}`
const getTrackName = (track: QueueTrackItem) => track.title
const getTrackArtist = (track: QueueTrackItem) => track.artist ?? track.album_title

/** 配信日の昇順比較（未設定は最初に寄せる） */
const compareByReleaseDate = (a: QueueTrackItem, b: QueueTrackItem) => {
  const left = a.release_date ?? ''
  const right = b.release_date ?? ''
  if (left === right) return 0
  return left < right ? -1 : 1
}

/** 日付系の並び順だけ比較関数を使い、名前系は頭文字セクションに任せる */
const SORT_COMPARATORS: Partial<Record<SortKey, (a: QueueTrackItem, b: QueueTrackItem) => number>> = {
  release: compareByReleaseDate,
}

const getReleaseYear = (track: QueueTrackItem) => track.release_date?.slice(0, 4) ?? '不明'

/** 配信日順は頭文字の代わりに年でセクション分けする */
const SORT_SECTION_KEYS: Partial<Record<SortKey, (track: QueueTrackItem) => string>> = {
  release: getReleaseYear,
}

const renderTrackRow = (track: QueueTrackItem, onPlay: () => void, isDownloaded: boolean, downloadedOnly: boolean) => (
  <div className="flex h-full items-center pl-4">
    <button
      type="button"
      onClick={onPlay}
      className="flex h-full min-w-0 flex-1 items-center gap-3 border-none text-left"
    >
      <span className="block size-12 shrink-0 overflow-hidden rounded-sm bg-white/10">
        {track.thumbnail ? (
          <AuthImage
            src={track.thumbnail}
            alt={track.album_title}
            className="size-full object-cover"
            cacheKey={`music-album-${track.product_id}`}
          />
        ) : (
          <span className="flex size-full items-center justify-center">
            <Music size={18} className="text-muted-foreground" />
          </span>
        )}
      </span>
      <span className="flex h-full min-w-0 flex-1 items-center border-b border-white/10">
        <span className="block min-w-0 flex-1">
          <span className="block truncate">{track.title}</span>
          <span className="block truncate text-sm text-muted-foreground">{track.artist ?? track.album_title}</span>
          {track.lyrics_first_line && (
            <span className="block truncate text-xs text-muted-foreground/70">{track.lyrics_first_line}</span>
          )}
        </span>
      </span>
    </button>
    <span className="flex h-full items-center gap-2 border-b border-white/10 pr-4">
      {isDownloaded && <CircleArrowDown size={18} className="shrink-0 text-muted-foreground" />}
      <MusicTrackMenu track={toMusicTrack(track)} downloadedOnly={downloadedOnly} />
    </span>
  </div>
)

export const MusicSongsPage = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const playQueue = useMusicStore((s) => s.playQueue)
  const offlineTracks = useOfflineMusicTracks()

  // ダウンロード済みライブラリからの遷移では、オフライン保存済みの曲だけを表示する
  const isDownloadedOnly = searchParams.get(DOWNLOADED_ONLY_PARAM) === '1'

  const [tracks, setTracks] = useState<QueueTrackItem[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  const [filter, setFilter] = useState<SongsFilter>(EMPTY_SONGS_FILTER)
  const [sort, setSort] = useState<SongsSort>(DEFAULT_SONGS_SORT)
  const [isFilterOpen, setIsFilterOpen] = useState(false)

  useEffect(() => {
    // ダウンロード済み表示は IndexedDB だけで組み立てられるためサーバーに問い合わせない
    if (isDownloadedOnly) {
      setIsLoading(false)
      return
    }

    let cancelled = false
    setIsLoading(true)
    setError(null)
    getQueueTracks(toQueryParams(filter))
      .then((res) => {
        if (cancelled) return
        setTracks(res.tracks)
      })
      .catch(() => {
        if (cancelled) return
        setError('曲の取得に失敗しました')
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [isDownloadedOnly, filter])

  const offlineIds = useMemo(() => new Set((offlineTracks ?? []).map((t) => t.episode_id)), [offlineTracks])

  const filteredTracks = useMemo(() => {
    const base = isDownloadedOnly ? (offlineTracks ?? []) : tracks
    const byDownload =
      filter.downloadState === 'all'
        ? base
        : base.filter((t) => offlineIds.has(t.episode_id) === (filter.downloadState === 'downloaded'))
    const keyword = query.trim().toLowerCase()
    if (!keyword) return byDownload
    return byDownload.filter(
      (t) => t.title.toLowerCase().includes(keyword) || (t.artist ?? '').toLowerCase().includes(keyword),
    )
  }, [tracks, query, isDownloadedOnly, offlineTracks, filter.downloadState, offlineIds])

  const isPending = isDownloadedOnly ? offlineTracks === null : isLoading
  const hasActiveFilter = isSongsFilterActive(filter)

  const handleSortClick = (key: SortKey) => {
    setSort((prev) =>
      prev.key === key ? { key, descending: !prev.descending } : { key, descending: defaultDescending(key) },
    )
  }

  // 曲を直接選んだときは1曲だけ再生する（続けて聴きたい場合は上の再生/シャッフルを使う）
  const handlePlayFrom = (track: QueueTrackItem) => {
    playQueue([toMusicTrack(track)], 0)
  }

  const handlePlayAll = () => {
    playQueue(sliceQueueWindow(filteredTracks, 0).items.map(toMusicTrack), 0)
  }

  const handleShuffle = () => {
    playQueue(pickRandomTracks(filteredTracks).map(toMusicTrack), 0, { shuffle: true })
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <Header title="曲" onClose={() => navigate(isDownloadedOnly ? PATHS.MUSIC.DOWNLOADED : PATHS.MUSIC.LIBRARY)} />

      <div className="shrink-0 space-y-3 px-4 py-3">
        <div className="flex items-center gap-2">
          <SearchBox value={query} onChange={(e) => setQuery(e.target.value)} placeholder="検索" />
          {/* ダウンロード済み一覧は IndexedDB 由来でサーバー絞り込みができないため出さない */}
          {!isDownloadedOnly && (
            <button
              type="button"
              onClick={() => setIsFilterOpen(true)}
              aria-label="フィルタ"
              className="flex size-9 shrink-0 items-center justify-center rounded-lg border-none bg-white/10 p-0!"
              style={hasActiveFilter ? { backgroundColor: ACCENT_COLOR, color: '#fff' } : undefined}
            >
              <SlidersHorizontal size={18} className={hasActiveFilter ? undefined : 'text-muted-foreground'} />
            </button>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-x-1 text-xs text-muted-foreground">
          <span>並び順：[</span>
          {SORT_OPTIONS.map((option, index) => {
            const isActive = sort.key === option.key
            return (
              <Fragment key={option.key}>
                {index !== 0 && <span className="text-white/20">|</span>}
                <button
                  type="button"
                  onClick={() => handleSortClick(option.key)}
                  className={`border-none bg-transparent px-1! py-0! text-xs! ${isActive ? 'font-semibold' : 'text-muted-foreground'}`}
                  style={isActive ? { color: ACCENT_COLOR } : undefined}
                >
                  {isActive && (sort.descending ? '▼' : '▲')}
                  {option.label}
                </button>
              </Fragment>
            )
          })}
          <span>]</span>
        </div>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={handlePlayAll}
            className="flex flex-1 items-center justify-center gap-2 rounded-lg border-none bg-white/10 py-3 font-semibold"
            style={{ color: ACCENT_COLOR }}
          >
            <Play size={18} fill={ACCENT_COLOR} />
            再生
          </button>
          <button
            type="button"
            onClick={handleShuffle}
            className="flex flex-1 items-center justify-center gap-2 rounded-lg border-none bg-white/10 py-3 font-semibold"
            style={{ color: ACCENT_COLOR }}
          >
            <Shuffle size={18} />
            シャッフル
          </button>
        </div>
      </div>

      {isPending && (
        <div className="flex flex-1 justify-center py-12">
          <Spinner size={40} />
        </div>
      )}
      {!isPending && error && <div className="flex-1 py-12 text-center text-muted-foreground">{error}</div>}
      {!isPending && !error && filteredTracks.length === 0 && (
        <div className="flex-1 py-12 text-center text-muted-foreground">曲が見つかりません</div>
      )}
      {!isPending && !error && filteredTracks.length > 0 && (
        <IndexedList
          items={filteredTracks}
          getKey={getTrackKey}
          getName={sort.key === 'artist' ? getTrackArtist : getTrackName}
          compare={SORT_COMPARATORS[sort.key]}
          getSectionKey={SORT_SECTION_KEYS[sort.key]}
          descending={sort.descending}
          rowHeight={TRACK_ROW_HEIGHT}
          renderRow={(track) =>
            renderTrackRow(track, () => handlePlayFrom(track), offlineIds.has(track.episode_id), isDownloadedOnly)
          }
        />
      )}

      <SongsFilterSheet open={isFilterOpen} onOpenChange={setIsFilterOpen} value={filter} onApply={setFilter} />
    </div>
  )
}
